import type { ApprovalKind, ApprovalRequestRecord, ApprovalStatus } from '@nadoutong/contracts';
import {
  parseCreditApplicationPayload,
  parseDiscountBelowSalesCostPayload,
  parseGiftOverQuotaPayload,
  parseTemporaryCreditApplicationPayload,
} from '@nadoutong/contracts';
import { formatCnyCents } from '../../components/formatters.ts';
import type { I18nKey } from '../../i18n/index.ts';
import { summarizeApprovalPayload } from '../../lib/approvals.ts';
import type { ChipTone } from '../../ui/index.ts';

/**
 * Per-kind presentation maps for the approval center (extracted from
 * ApprovalCenterPage.tsx in E3-G2 #1103, #1098 deferred item 18).
 *
 * Label/status maps are Record<union, key> so a new kind or status
 * fails the typecheck until it gets a label; the payload summarizer is
 * per-kind with the kind-agnostic `summarizeApprovalPayload` default —
 * kinds add a summarizer when their payload deserves a human line.
 */

export const KIND_LABEL_KEYS: Record<ApprovalKind, I18nKey> = {
  customer_transfer: 'approvals.kind.customer_transfer',
  gift_over_quota: 'approvals.kind.gift_over_quota',
  discount_below_sales_cost: 'approvals.kind.discount_below_sales_cost',
  credit_application: 'approvals.kind.credit_application',
  temporary_credit_application: 'approvals.kind.temporary_credit_application',
};

export const STATUS_LABEL_KEYS: Record<ApprovalStatus, I18nKey> = {
  pending: 'approvals.status.pending',
  approved: 'approvals.status.approved',
  rejected: 'approvals.status.rejected',
  cancelled: 'approvals.status.cancelled',
};

/**
 * Org-scoped kinds (`customer_transfer`, `gift_over_quota`) put the org id
 * on `target_id`. Credit kinds and `discount_below_sales_cost` freeze
 * `org_id` in the payload instead — their `target_id` is the application
 * row or the `org:sku` composite.
 */
export function approvalOrgTargetId(request: ApprovalRequestRecord): string | null {
  if (request.target_type === 'org') {
    return request.target_id;
  }
  if (
    request.kind === 'credit_application' ||
    request.kind === 'temporary_credit_application' ||
    request.kind === 'discount_below_sales_cost'
  ) {
    const orgId = request.payload.org_id;
    return typeof orgId === 'string' && orgId.length > 0 ? orgId : null;
  }
  return null;
}

export function statusChipTone(status: ApprovalStatus): ChipTone {
  if (status === 'approved') return 'ok';
  if (status === 'rejected') return 'bad';
  if (status === 'pending') return 'warn';
  return 'neutral';
}

type KindPayloadSummarizer = (payload: Record<string, unknown>) => string | null;

/**
 * `gift_over_quota` (#1103): face value first — the number the lead is
 * authorizing — then the requester's reason. Falls back to the generic
 * summary when the payload does not parse (out-of-band rows).
 */
function summarizeGiftOverQuota(payload: Record<string, unknown>): string | null {
  const parsed = parseGiftOverQuotaPayload(payload);
  if (parsed === null) {
    return null;
  }
  const amount = formatCnyCents(parsed.amount_cny_cents, false);
  return parsed.reason === undefined ? amount : `${amount} · ${parsed.reason}`;
}

const KIND_PAYLOAD_SUMMARIZERS: Partial<Record<ApprovalKind, KindPayloadSummarizer>> = {
  gift_over_quota: summarizeGiftOverQuota,
  discount_below_sales_cost: summarizeDiscountBelowSalesCost,
  credit_application: summarizeCreditApplication,
  temporary_credit_application: summarizeTemporaryCreditApplication,
};

/**
 * `discount_below_sales_cost` (#1106): the SKU, then the proposed rate
 * against the sales-floor snapshot the lead is authorizing under, then
 * the requester's reason. Sales-floor values are safe on every surface
 * (the payload never carries procurement); falls back to the generic
 * summary when the payload does not parse (out-of-band rows).
 */
function summarizeDiscountBelowSalesCost(payload: Record<string, unknown>): string | null {
  const parsed = parseDiscountBelowSalesCostPayload(payload);
  if (parsed === null) {
    return null;
  }
  const line =
    `${parsed.sku_name} · ${parsed.proposed_rate_multiplier.toFixed(4)} ` +
    `< floor ${parsed.sales_floor_multiplier.toFixed(4)}`;
  return parsed.reason === undefined ? line : `${line} · ${parsed.reason}`;
}

/**
 * `credit_application` (#1126): the requested limit + cycle the deciders
 * (lead then finance) are authorizing beyond the platform defaults.
 * Falls back to the generic summary when the payload does not parse.
 */
function summarizeCreditApplication(payload: Record<string, unknown>): string | null {
  const parsed = parseCreditApplicationPayload(payload);
  if (parsed === null) {
    return null;
  }
  return `${formatCnyCents(parsed.requested_limit_cny_cents, false)} · ${parsed.requested_cycle_days}d`;
}

function summarizeTemporaryCreditApplication(payload: Record<string, unknown>): string | null {
  const parsed = parseTemporaryCreditApplicationPayload(payload);
  if (parsed === null) {
    return null;
  }
  return `${formatCnyCents(parsed.requested_limit_cny_cents, false)} · ${parsed.expected_arrival_date}`;
}

/** Queue-row payload summary: per-kind renderer, generic fallback. */
export function summarizeApprovalPayloadForKind(
  kind: ApprovalKind,
  payload: Record<string, unknown>,
): string {
  const summarizer = KIND_PAYLOAD_SUMMARIZERS[kind];
  const summary = summarizer === undefined ? null : summarizer(payload);
  return summary ?? summarizeApprovalPayload(payload);
}
